<template>
  <ContentBlock class="discoverable-block" :required="true">
    <div slot="instructions">
      <h5>{{ $t('Make Content Discoverable') }}</h5>
      <p>{{ $t("This information will help locations and players find your content.") }}</p>
    </div>
    <div class="discoverable">
      <div class="content-target">
        <h2 class="f8">
          {{ $t('My content was built primarily for') }}
          <span class="required">*</span>
        </h2>
        <UIRadio
          :title="$t('Entertainment')"
          name="contentTarget"
          id="contentTarget"
          :value="contentOwnership.contentTarget === 'entertainment'"
          @change="contentOwnership.contentTarget = 'entertainment'"
        />
        <UIRadio
          :title="$t('Education')"
          name="contentTarget"
          id="contentTarget"
          :value="contentOwnership.contentTarget === 'education'"
          @change="contentOwnership.contentTarget = 'education'"
        />
        <UIRadio
          :title="$t('Narrative')"
          name="contentTarget"
          id="contentTarget"
          :value="contentOwnership.contentTarget === 'narrative'"
          @change="contentOwnership.contentTarget = 'narrative'"
        />
      </div>
      <UISelect
        :title="$t('Difficulty')"
        :placeholder="$t('Choose a Difficulty')"
        v-model="contentOwnership.difficulty"
        required
      >
        <option value="1">{{ $t('Easy') }}</option>
        <option value="2">{{ $t('Medium') }}</option>
        <option value="3">{{ $t('Hard') }}</option>
      </UISelect>
      <UISelect
        :title="$t('Age Rating')"
        v-model="contentOwnership.ageRating"
        :placeholder="$t('Choose an Age Rating')"
        :instructions="$t('The content will be rated appropriate for the selected age or older.')"
        required
      >
        <option :value="0">{{ $t('No Age Rating') }}</option>
        <option v-for="i in 21" :value="i" :key="i">{{ i }}+</option>
      </UISelect>
      <div class="input-group">
        <UIMultiselect
          :title=" $t('Genres (up to 5)')"
          :taggable="false"
          v-model="contentOwnership.genres"
          :options="genreOptions"
          label="title"
          track-by="id"
          :multiple="true"
          required
          :max="5"
        />
      </div>
      <div class="players">
        <div class="number-of-players">
          {{ $t('Number of Players') }}
          <span class="required">*</span>
        </div>
        <UISwitch :title="$t('Single Player')" v-model="contentOwnership.singleplayer" />
        <UISwitch :title="$t('Multiplayer')" v-model="contentOwnership.multiplayer" />
        <UIInput
          type="number"
          min="1"
          v-if="contentOwnership.multiplayer"
          :title="$t('Minimum Number of Players')"
          v-model="contentOwnership.minPlayers"
          required
        />
        <UIInput
          type="number"
          min="1"
          v-if="contentOwnership.multiplayer"
          :title="$t('Maximum Number of Players')"
          v-model="contentOwnership.maxPlayers"
          required
        />
      </div>
    </div>
  </ContentBlock>
</template>

<script>

import ContentBlock from '@/core/shared/components/ContentBlock';
import UISwitch from '@/core/shared/components/ui/UISwitch';
import UISelect from '@/core/shared/components/ui/UISelect';
import UIInput from '@/core/shared/components/ui/UIInput';
import UIMultiselect from '@/core/shared/components/ui/UIMultiselect';
import UIRadio from '@/core/shared/components/ui/UIRadio';
import { ref, watch } from '@vue/composition-api';
import store from '@/core/store';

export default {
  name: 'ContentDiscoverableForm',
  components: {
    ContentBlock,
    UISwitch,
    UISelect,
    UIInput,
    UIMultiselect,
    UIRadio,
  },
  props: {
    contentOwnership: {
      type: Object,
      required: true,
    },
  },
  setup (props) {
    const genreOptions = ref([]);

    (async function init () {
      genreOptions.value = await store.dispatch('getGenres');
    })();

    watch(() => props.contentOwnership?.multiplayer, (to, from) => {
      if (!from && to) {
        props.contentOwnership.minPlayers = props.contentOwnership.minPlayers || 1;
        props.contentOwnership.maxPlayers = props.contentOwnership.maxPlayers || 1;
      }
    }, { immediate: true });

    return {
      genreOptions,
    };
  },

};
</script>

<style lang="postcss" scoped>
@import "core/shared/styles";

.discoverable-block {
  z-index: 2;
}

.discoverable {
  display: grid;
  grid-gap: var(--spacingMd);

  @media (--tablet) {
    grid-template-columns: repeat(2, 1fr);
  }

  & .required {
    color: var(--colorSBRedFlat);
  }

  & .players {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: var(--spacingSm);
    margin-bottom: var(--spacingSm);

    @media (--tablet) {
      grid-column: span 2;
    }

    & .number-of-players {
      @apply --f8;
      grid-column: 1 / 3;
    }
  }

  & .input-group {
    @apply --inputGroup;

    & .label {
      @apply --f8;
    }
  }

  & .content-target {
    display: grid;
    grid-gap: var(--spacingXS);
  }
}
</style>
